import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import NavBar from "@/components/NavBar";
import CommentThread from "@/components/CommentThread";
import EmojiButton from "@/components/EmojiButton";
import PriceSlider from "@/components/PriceSlider";
import ShareModal from "@/components/ShareModal";
import PricingFeedbackForm from "@/components/PricingFeedbackForm";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import {
  useCampaign,
  useCampaignAssets,
  useCampaignComments,
  useSubscribeToWaitlist,
  useWaitlistCount,
  usePricingFeedback,
  useCreateMultipleResponses,
  useHasUserRespondedToCampaign,
} from "@/hooks/useApi";
import { Loader2, ScrollText, WalletCards } from "lucide-react";
import {
  ArrowRight,
  ArrowLeft,
  MessageCircle,
  ThumbsUp,
  ChartBar,
  Share,
  Pencil,
  Image,
  Video,
  Link as LinkIcon,
  Trash2,
  Upload,
  X,
  Maximize,
  Star,
  Users,
  Calendar,
  TrendingUp,
  Eye,
  Sparkles,
  CheckCircle,
  Clock,
  Target,
  Zap,
  Settings,
  QrCode,
} from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// TypeScript interfaces
interface User {
  id: string;
  name: string;
  avatarUrl?: string;
}

interface CommentReply {
  id: string;
  user: User;
  content: string;
  createdAt: string;
  likes: number;
  replies: CommentReply[];
  tags?: string[];
}

interface Comment {
  id: string;
  user: User;
  content: string;
  createdAt: string;
  likes: number;
  replies: CommentReply[];
}

// Use the Question interface from the API
import { Question, campaignsApi } from "@/lib/campaignsApi";

interface Asset {
  id: string;
  type: "image" | "video" | "link" | "file";
  url: string;
  title: string;
  description?: string;
  file?: File;
}

interface NewAsset {
  type: "image" | "video" | "link" | "file";
  url: string;
  title: string;
  description: string;
}

// Use the Campaign interface from the API
import { Campaign } from "@/lib/campaignsApi";

interface QuestionResponses {
  [questionId: string]: string | number | string[];
}

// Remove mock data - will use real API data

const CampaignView = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, isAuthenticated } = useAuth();

  // Fetch campaign data from API
  const { data: campaignData, isLoading, error } = useCampaign(id!);

  const [activeTab, setActiveTab] = useState("overview");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<QuestionResponses>({});
  const [commentContent, setCommentContent] = useState("");
  const [email, setEmail] = useState("");
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Waitlist functionality
  const subscribeToWaitlistMutation = useSubscribeToWaitlist();
  const { data: waitlistCount } = useWaitlistCount(campaignData?.id || "");

  // Pricing feedback functionality
  const { data: pricingFeedback = [] } = usePricingFeedback(
    campaignData?.id || ""
  );

  // Response submission functionality
  const createMultipleResponsesMutation = useCreateMultipleResponses();
  const { data: hasUserResponded = false } = useHasUserRespondedToCampaign(
    campaignData?.id || ""
  );

  // Asset viewing states
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showAssetModal, setShowAssetModal] = useState(false);

  // New state for playground tab assets - only load when on resources tab
  const {
    data: campaignAssets = [],
    isLoading: assetsLoading,
    refetch: refetchAssets,
  } = useCampaignAssets(
    activeTab === "playground" ? campaignData?.id || "" : ""
  );

  // Comments - only load when on discuss tab
  const {
    data: campaignComments = [],
    isLoading: commentsLoading,
    refetch: refetchComments,
  } = useCampaignComments(
    activeTab === "discuss" ? campaignData?.id || "" : ""
  );

  const [newAsset, setNewAsset] = useState<NewAsset>({
    type: "image",
    url: "",
    title: "",
    description: "",
  });

  // Status management
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");

  // Status options
  const statusOptions = [
    {
      value: "DRAFT",
      label: "Draft",
      description: "Campaign is not yet active",
      color: "text-gray-500",
    },
    {
      value: "ACTIVE",
      label: "Active",
      description: "Campaign is live and collecting feedback",
      color: "text-green-600",
    },
    {
      value: "PAUSED",
      label: "Paused",
      description: "Campaign is temporarily paused",
      color: "text-yellow-600",
    },
    {
      value: "ARCHIVED",
      label: "Archived",
      description: "Campaign is no longer active",
      color: "text-red-600",
    },
  ];

  // Check if the current user is the creator of this campaign
  const isCreator = isAuthenticated && user?.id === campaignData?.creatorId;

  // Helper to map backend asset type to lowercase for frontend
  const mapAssetType = (type: string) => type.toLowerCase();

  // Helper function to initialize MULTIPLE type questions with empty arrays
  const initializeMultipleQuestion = (questionId: string) => {
    if (
      !responses[questionId] &&
      campaignData.questions.find((q) => q.id === questionId)?.type ===
        "MULTIPLE"
    ) {
      setResponses((prev) => ({
        ...prev,
        [questionId]: [],
      }));
    }
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                <p className="text-slate-600">Loading campaign data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error || !campaignData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-slate-900 mb-4">
                Campaign Not Found
              </h1>
              <p className="text-slate-600 mb-6">
                The campaign you're looking for doesn't exist or has been
                removed.
              </p>
              <Button onClick={() => navigate("/browse")}>
                Browse Campaigns
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleResponse = (
    questionId: string,
    answer: string | number | string[]
  ) => {
    setResponses((prev) => {
      const currentResponse = prev[questionId];

      // If this is a MULTIPLE type question and we're receiving a single option
      if (typeof answer === "string" && Array.isArray(currentResponse)) {
        // Toggle the option in the array
        const currentArray = currentResponse as string[];
        if (currentArray.includes(answer)) {
          // Remove the option if it's already selected
          return {
            ...prev,
            [questionId]: currentArray.filter((item) => item !== answer),
          };
        } else {
          // Add the option if it's not selected
          return {
            ...prev,
            [questionId]: [...currentArray, answer],
          };
        }
      }

      // For all other cases, replace the response
      return {
        ...prev,
        [questionId]: answer,
      };
    });
  };

  const nextQuestion = async () => {
    if (currentQuestionIndex < campaignData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Submit all responses to the backend
      try {
        const responseData = Object.entries(responses).map(
          ([questionId, answer]) => ({
            questionId,
            answer: Array.isArray(answer) ? answer.join(", ") : String(answer),
          })
        );

        if (responseData.length > 0) {
          await createMultipleResponsesMutation.mutateAsync({
            campaignId: campaignData.id,
            responses: responseData,
          });
        }

        setShowFeedbackForm(false);
        setResponses({});
        setCurrentQuestionIndex(0);

        toast({
          title: "Thanks for your feedback! 🎉",
          description: "Your insights help creators build better products.",
        });
      } catch (error: unknown) {
        console.error("Error submitting responses:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Please try again.";
        toast({
          title: "Error submitting feedback",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleAddComment = (content: string, parentId?: string) => {
    // In a real app, you would save this to your backend
    toast({
      description: "Your comment has been posted!",
    });
    setCommentContent("");
  };

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !campaignData?.id) return;

    try {
      await subscribeToWaitlistMutation.mutateAsync({
        campaignId: campaignData.id,
        data: { email },
      });
      setEmail("");
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  };

  const handleEditCampaign = () => {
    navigate(`/edit-campaign/${campaignData.id}`);
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;

    try {
      // Update campaign status via API using the campaignsApi
      await campaignsApi.updateCampaign(id!, {
        status: selectedStatus as "DRAFT" | "ACTIVE" | "PAUSED" | "ARCHIVED",
      });

      toast({
        title: "Status Updated",
        description: `Campaign status changed to ${selectedStatus}`,
      });
      setShowStatusDialog(false);
      // Refresh campaign data
      window.location.reload();
    } catch (error) {
      console.error("Status update error:", error);
      toast({
        title: "Error",
        description: "Failed to update campaign status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAddAsset = () => {
    if ((!newAsset.url && newAsset.type !== "file") || !newAsset.title) {
      toast({
        title: "Missing information",
        description:
          "Please provide at least a URL/file and title for your asset.",
        variant: "destructive",
      });
      return;
    }

    // In a real app, you would send this to your backend
    toast({
      title: "Asset added",
      description: "Your asset has been added to the playground.",
    });

    setNewAsset({
      type: "image",
      url: "",
      title: "",
      description: "",
    });
  };

  const handleDeleteAsset = async (assetId: string) => {
    try {
      await campaignsApi.deleteAsset(assetId);
      refetchAssets();
      toast({ description: "Asset has been removed" });
    } catch (error) {
      console.error("Error deleting asset:", error);
      toast({
        title: "Error",
        description: "Failed to delete asset. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleViewAsset = (asset: Asset) => {
    setSelectedAsset(asset);
    setShowAssetModal(true);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Create object URL for preview
    const objectUrl = URL.createObjectURL(file);

    // Determine if it's an image or video based on MIME type
    let assetType: "image" | "video" | "file" = "file";
    if (file.type.startsWith("image/")) {
      assetType = "image";
    } else if (file.type.startsWith("video/")) {
      assetType = "video";
    }

    setNewAsset({
      ...newAsset,
      type: assetType,
      url: objectUrl,
      title: file.name,
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const renderAssetPreview = (asset: Asset) => {
    switch (asset.type) {
      case "image":
        return (
          <AspectRatio
            ratio={16 / 9}
            className="bg-muted rounded-lg overflow-hidden cursor-pointer"
            onClick={() => handleViewAsset(asset)}
          >
            <img
              src={asset.url}
              alt={asset.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
              <Maximize className="w-8 h-8 text-white" />
            </div>
          </AspectRatio>
        );
      case "video":
        return (
          <AspectRatio
            ratio={16 / 9}
            className="bg-muted rounded-lg overflow-hidden"
          >
            <video
              src={asset.url}
              controls
              className="w-full h-full object-cover cursor-pointer"
              title={asset.title}
              onClick={() => handleViewAsset(asset)}
            />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100 pointer-events-none">
              <Maximize className="w-8 h-8 text-white" />
            </div>
          </AspectRatio>
        );
      case "link":
        return (
          <div
            className="flex items-center p-4 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors"
            onClick={() => handleViewAsset(asset)}
          >
            <LinkIcon className="mr-2 flex-shrink-0" />
            <a
              href={asset.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline text-ellipsis overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {asset.url}
            </a>
          </div>
        );
      case "file":
        return (
          <div
            className="flex items-center p-4 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors"
            onClick={() => handleViewAsset(asset)}
          >
            <Upload className="mr-2 flex-shrink-0" />
            <span className="text-ellipsis overflow-hidden">{asset.title}</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Function to render expanded asset in modal
  const renderExpandedAsset = () => {
    if (!selectedAsset) return null;

    switch (selectedAsset.type) {
      case "image":
        return (
          <div className="flex flex-col items-center">
            <div className="max-h-[70vh] overflow-hidden rounded-lg">
              <img
                src={selectedAsset.url}
                alt={selectedAsset.title}
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
          </div>
        );
      case "video":
        return (
          <div className="w-full max-h-[70vh]">
            <video
              src={selectedAsset.url}
              controls
              className="w-full max-h-[70vh]"
              title={selectedAsset.title}
            />
          </div>
        );
      case "link":
        return (
          <div className="flex flex-col items-center">
            <div className="bg-muted/30 p-6 rounded-lg w-full text-center">
              <LinkIcon className="h-12 w-12 mx-auto mb-4 text-primary" />
              <h3 className="text-xl font-semibold mb-2">
                {selectedAsset.title}
              </h3>
              <a
                href={selectedAsset.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline break-all"
              >
                {selectedAsset.url}
              </a>
              <Button
                className="mt-6"
                onClick={() =>
                  window.open(
                    selectedAsset.url,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                Open Link
              </Button>
            </div>
          </div>
        );
      case "file":
        return (
          <div className="flex flex-col items-center">
            <div className="bg-muted/30 p-6 rounded-lg w-full text-center">
              <Upload className="h-12 w-12 mx-auto mb-4 text-primary" />
              <h3 className="text-xl font-semibold mb-2">
                {selectedAsset.title}
              </h3>
              <p className="text-text-secondary mb-4">File Preview</p>
              <Button className="mt-2">Download File</Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderQuestion = (question: Question, index: number) => {
    if (index !== currentQuestionIndex) return null;

    // Initialize MULTIPLE type questions with empty arrays
    if (question.type === "MULTIPLE") {
      initializeMultipleQuestion(question.id!);
    }

    switch (question.type) {
      case "EMOJI":
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">{question.text}</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {/* Use custom emoji options if available, otherwise use defaults */}
              {(question.options && question.options.length > 0
                ? question.options
                : ["😍", "🙂", "😐", "😕", "👎"]
              ).map((option, optionIndex) => {
                const defaultLabels = [
                  "Love it!",
                  "Like it",
                  "Neutral",
                  "Unsure",
                  "Not for me",
                ];
                const label =
                  question.options && question.options.length > 0
                    ? `Option ${optionIndex + 1}`
                    : defaultLabels[optionIndex] || `Option ${optionIndex + 1}`;

                return (
                  <EmojiButton
                    key={optionIndex}
                    emoji={option}
                    label={label}
                    isSelected={responses[question.id] === option}
                    onClick={() => {
                      handleResponse(question.id, option);
                      setTimeout(nextQuestion, 500);
                    }}
                  />
                );
              })}
            </div>
          </div>
        );

      case "MULTIPLE":
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">{question.text}</h3>
            <div className="space-y-2">
              {question.options?.map((option: string) => {
                // Initialize response as array if not already set
                const currentResponse = responses[question.id];
                const selectedOptions = Array.isArray(currentResponse)
                  ? currentResponse
                  : [];
                const isSelected = selectedOptions.includes(option);

                return (
                  <div
                    key={option}
                    className={`p-3 border border-surface-border rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? "border-primary-500 bg-primary-50"
                        : "hover:border-primary-200"
                    }`}
                    onClick={() => {
                      handleResponse(question.id, option);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span>{option}</span>
                      {isSelected && (
                        <div className="w-5 h-5 bg-primary-500 text-white rounded-full flex items-center justify-center text-xs">
                          ✓
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {Array.isArray(responses[question.id]) &&
              (responses[question.id] as string[]).length > 0 && (
                <div className="text-sm text-primary-600 font-medium">
                  {(responses[question.id] as string[]).length} option
                  {(responses[question.id] as string[]).length === 1 ? "" : "s"}{" "}
                  selected
                </div>
              )}
            <Button
              onClick={nextQuestion}
              disabled={
                !responses[question.id] ||
                (Array.isArray(responses[question.id]) &&
                  (responses[question.id] as string[]).length === 0)
              }
              className="w-full mt-4"
            >
              {currentQuestionIndex === campaignData.questions.length - 1
                ? "Submit Feedback"
                : "Next"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );

      case "RATING":
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">{question.text}</h3>

            {/* Rating scale display */}
            <div className="flex justify-center">
              <div className="flex items-center gap-2">
                {Array.from(
                  { length: parseInt(String(question.ratingScale || "5")) },
                  (_, i) => {
                    const rating = i + 1;
                    const isSelected = responses[question.id] === rating;

                    return (
                      <button
                        key={rating}
                        onClick={() => {
                          handleResponse(question.id, rating);
                        }}
                        className={`w-12 h-12 rounded-lg border-2 transition-all duration-200 flex items-center justify-center text-lg font-semibold ${
                          isSelected
                            ? "border-primary-500 bg-primary-500 text-white shadow-lg"
                            : "border-slate-200 hover:border-primary-300 hover:bg-primary-50"
                        }`}
                      >
                        {rating}
                      </button>
                    );
                  }
                )}
              </div>
            </div>

            {/* Rating labels */}
            <div className="flex justify-between text-sm text-slate-600 px-2">
              <span>1</span>
              <span>{parseInt(String(question.ratingScale || "5"))}</span>
            </div>

            <Button
              onClick={nextQuestion}
              disabled={!responses[question.id]}
              className="w-full mt-4"
            >
              {currentQuestionIndex === campaignData.questions.length - 1
                ? "Submit Feedback"
                : "Next"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );

      case "TEXT":
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">{question.text}</h3>
            <Textarea
              placeholder="Share your thoughts..."
              value={responses[question.id] || ""}
              onChange={(e) => handleResponse(question.id, e.target.value)}
              className="min-h-32"
            />
            <Button
              onClick={nextQuestion}
              disabled={!responses[question.id]}
              className="w-full"
            >
              {currentQuestionIndex === campaignData.questions.length - 1
                ? "Submit Feedback"
                : "Next"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">{question.text}</h3>
            <div className="text-center text-slate-500">
              <p>This question type is not yet supported.</p>
            </div>
          </div>
        );
    }
  };

  const showCampaignReport = () => {
    navigate(`/campaign/${campaignData.id}/report`);
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
    hover: {
      y: -4,
      boxShadow:
        "0 20px 40px -8px rgba(99, 102, 241, 0.15), 0 8px 16px -8px rgba(0, 0, 0, 0.1)",
      transition: { duration: 0.3 },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <NavBar />

      {/* Asset Viewing Modal */}
      <Dialog open={showAssetModal} onOpenChange={setShowAssetModal}>
        <DialogContent className="max-w-4xl w-full p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {selectedAsset?.title}
            </DialogTitle>
            {selectedAsset?.description && (
              <DialogDescription className="text-lg">
                {selectedAsset.description}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="mt-4">{renderExpandedAsset()}</div>
        </DialogContent>
      </Dialog>

      <div className="container mx-auto px-4 pt-20 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Enhanced Campaign Banner */}
          <motion.div
            className="rounded-2xl overflow-hidden mb-8 shadow-2xl relative group min-h-[300px] sm:min-h-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <AspectRatio
              ratio={3 / 1}
              className="bg-gradient-to-r from-primary-600 to-accent-600 min-h-[300px] sm:min-h-0"
            >
              <img
                src={campaignData.bannerImage}
                alt={campaignData.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent">
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8 text-white">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="bg-white/20 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
                      {campaignData.category || "Productivity"}
                    </div>
                    <div className="bg-emerald-500/80 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium flex items-center">
                      <Star className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 fill-white" />
                      {campaignData.status === "ACTIVE"
                        ? "Active"
                        : campaignData.status}
                    </div>
                  </div>

                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-sora mb-3 sm:mb-4 leading-tight">
                    {campaignData.title}
                  </h1>
                  <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-4 sm:mb-6 max-w-3xl leading-relaxed">
                    {campaignData.tagline}
                  </p>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-white/80">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                      <span className="text-xs sm:text-sm font-medium">
                        Created{" "}
                        {new Date(campaignData.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                      <span className="text-xs sm:text-sm font-medium">
                        by {campaignData.creator.name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Creator Controls */}
              <motion.div
                className="mb-6 absolute top-3 sm:top-6 left-3 sm:left-6"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Button
                  variant="ghost"
                  onClick={() => navigate(-1)}
                  size="sm"
                  className="bg-white/20 hover:bg-white/30 text-white rounded-xl backdrop-blur-sm border border-white/20 text-xs sm:text-sm"
                >
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
              </motion.div>
              {isCreator && (
                <div className="absolute top-3 sm:top-6 right-3 sm:right-6 flex gap-1 sm:gap-2">
                  <ShareModal
                    content={{
                      id: campaignData.id,
                      title: campaignData.title,
                      url: `${window.location.origin}/campaign/${campaignData.id}`,
                      description: campaignData.description,
                      type: "campaign",
                    }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="bg-white/20 hover:bg-white/30 text-white rounded-xl backdrop-blur-sm border border-white/20 text-xs sm:text-sm"
                    >
                      <QrCode className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Share</span>
                    </Button>
                  </ShareModal>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedStatus(campaignData.status || "DRAFT");
                      setShowStatusDialog(true);
                    }}
                    className="bg-white/20 hover:bg-white/30 text-white rounded-xl backdrop-blur-sm border border-white/20 text-xs sm:text-sm"
                  >
                    <Settings className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Status</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditCampaign}
                    className="bg-white/20 hover:bg-white/30 text-white rounded-xl backdrop-blur-sm border border-white/20 text-xs sm:text-sm"
                  >
                    <Pencil className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                </div>
              )}

              {/* Share Button for All Users */}
              {!isCreator && (
                <div className="absolute top-3 sm:top-6 right-3 sm:right-6">
                  <ShareModal
                    content={{
                      id: campaignData.id,
                      title: campaignData.title,
                      url: `${window.location.origin}/campaign/${campaignData.id}`,
                      description: campaignData.description,
                      type: "campaign",
                    }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="bg-white/20 hover:bg-white/30 text-white rounded-xl backdrop-blur-sm border border-white/20 text-xs sm:text-sm"
                    >
                      <QrCode className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Share</span>
                    </Button>
                  </ShareModal>
                </div>
              )}
            </AspectRatio>
          </motion.div>

          {/* Enhanced Stats & Action Bar */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            {/* Stats Cards */}
            <motion.div
              className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/50 shadow-lg"
              variants={cardVariants}
              whileHover="hover"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 sm:p-3 bg-primary-100 rounded-xl">
                  <ThumbsUp className="w-4 h-4 sm:w-6 sm:h-6 text-primary-600" />
                </div>
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary-500" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                {campaignData._count.responses}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">
                Total Responses
              </div>
            </motion.div>

            <motion.div
              className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/50 shadow-lg"
              variants={cardVariants}
              whileHover="hover"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 sm:p-3 bg-accent-100 rounded-xl">
                  <MessageCircle className="w-4 h-4 sm:w-6 sm:h-6 text-accent-600" />
                </div>
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                {campaignData._count.comments}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">
                Comments
              </div>
            </motion.div>

            <motion.div
              className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/50 shadow-lg"
              variants={cardVariants}
              whileHover="hover"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 sm:p-3 bg-emerald-100 rounded-xl">
                  <Eye className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-600" />
                </div>
                <WalletCards className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                {campaignData.pricingOptions?.length || 0}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 font-medium">
                Pricing Options
              </div>
            </motion.div>

            {isCreator && (
              <motion.div
                className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/50 shadow-lg"
                variants={cardVariants}
                whileHover="hover"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 sm:p-3 bg-blue-100 rounded-xl">
                    <Users className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                  </div>
                  <ScrollText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                  {waitlistCount?.count || 0}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">
                  Waitlist
                </div>
              </motion.div>
            )}

            {/* Action Card */}
            <motion.div
              className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-4 sm:p-6 text-white shadow-lg"
              variants={cardVariants}
              whileHover="hover"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 sm:p-3 bg-white/20 rounded-xl">
                  <Target className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300" />
              </div>
              <div className="text-xs sm:text-sm font-medium mb-3">
                Ready to engage?
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowFeedbackForm(!showFeedbackForm)}
                className="w-full bg-white/20 hover:bg-white/30 text-white border-0 text-xs sm:text-sm"
              >
                {showFeedbackForm ? "Hide Form" : "Give Feedback"}
              </Button>
            </motion.div>
          </motion.div>

          {/* Enhanced Action Buttons */}
          <motion.div
            className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/50 p-4 sm:p-6 mb-8 shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => setShowFeedbackForm(!showFeedbackForm)}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg px-4 sm:px-8 text-sm sm:text-base"
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                {hasUserResponded
                  ? "View Your Feedback"
                  : showFeedbackForm
                  ? "Hide Feedback Form"
                  : "Share Your Thoughts"}
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={() => setActiveTab("discuss")}
                className="border-2 border-primary-200 hover:border-primary-300 hover:bg-primary-50 px-4 sm:px-8 text-sm sm:text-base"
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                Join Discussion
              </Button>

              <ShareModal
                content={{
                  id: campaignData.id,
                  title: campaignData.title,
                  url: `${window.location.origin}/campaign/${campaignData.id}`,
                  description: campaignData.description,
                  type: "campaign",
                }}
              >
                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-primary-200 hover:border-primary-300 hover:bg-primary-50 px-4 sm:px-8 text-sm sm:text-base"
                >
                  <QrCode className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                  Share Campaign
                </Button>
              </ShareModal>

              {isCreator && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={showCampaignReport}
                  className="border-2 border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50 text-emerald-700 px-4 sm:px-8 text-sm sm:text-base"
                >
                  <ChartBar className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                  View Analytics
                </Button>
              )}
            </div>
          </motion.div>

          {/* Enhanced Feedback Form */}
          {showFeedbackForm && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="mb-8"
            >
              <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl rounded-2xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-accent-50 border-b border-gray-100">
                  <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900 flex flex-col sm:flex-row sm:items-center">
                    <div className="p-2 sm:p-0 sm:mr-3 mb-2 sm:mb-0 self-start">
                      <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                    </div>
                    Share Your Valuable Feedback
                  </CardTitle>
                  <CardDescription className="text-base sm:text-lg text-gray-600">
                    Your insights help shape the future of innovative products
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 lg:p-8">
                  {hasUserResponded ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">✅</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        You've already provided feedback!
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Thank you for your valuable insights. You can only
                        submit feedback once per campaign.
                      </p>
                      <Button
                        size="lg"
                        onClick={() => setShowFeedbackForm(false)}
                        className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Close
                      </Button>
                    </div>
                  ) : (
                    <>
                      {renderQuestion(
                        campaignData.questions[currentQuestionIndex],
                        currentQuestionIndex
                      )}

                      {currentQuestionIndex ===
                        campaignData.questions.length && (
                        <div className="space-y-6 text-center">
                          <motion.div
                            className="text-6xl"
                            animate={{
                              scale: [1, 1.2, 1],
                              rotate: [0, 10, -10, 0],
                            }}
                            transition={{ duration: 1 }}
                          >
                            🎉
                          </motion.div>
                          <h3 className="text-2xl font-bold text-gray-900">
                            Thank you for your feedback!
                          </h3>
                          <p className="text-gray-600 text-lg">
                            Your insights help creators build better products
                            for everyone.
                          </p>
                          <Button
                            size="lg"
                            onClick={() => setShowFeedbackForm(false)}
                            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                          >
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Explore Campaign Details
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Enhanced Main Content Tabs */}
          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl rounded-2xl overflow-hidden">
            <Tabs
              defaultValue="overview"
              value={activeTab}
              onValueChange={setActiveTab}
            >
              <div className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b border-gray-100">
                <TabsList className="w-full bg-transparent border-0 p-0 h-auto rounded-none overflow-x-auto">
                  <TabsTrigger
                    value="overview"
                    className="flex-1 py-3 sm:py-6 px-3 sm:px-8 text-sm sm:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap"
                  >
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Overview</span>
                    <span className="sm:hidden">Overview</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="discuss"
                    className="flex-1 py-3 sm:py-6 px-3 sm:px-8 text-sm sm:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap"
                  >
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Discussion</span>
                    <span className="sm:hidden">Discuss</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="pricing"
                    className="flex-1 py-3 sm:py-6 px-3 sm:px-8 text-sm sm:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap"
                  >
                    <ChartBar className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Pricing</span>
                    <span className="sm:hidden">Price</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="playground"
                    className="flex-1 py-3 sm:py-6 px-3 sm:px-8 text-sm sm:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap"
                  >
                    <Image className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Resources</span>
                    <span className="sm:hidden">Files</span>
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" className="p-4 sm:p-6 lg:p-8">
                <div className="space-y-10">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center mb-4 sm:mb-6">
                      <div className="p-2 sm:p-3 bg-primary-100 rounded-xl mr-0 sm:mr-4 mb-3 sm:mb-0 self-start">
                        <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                          About This Innovation
                        </h3>
                        <p className="text-gray-600 text-sm sm:text-base">
                          Discover what makes this idea special
                        </p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-primary-50 to-accent-50 p-4 sm:p-6 lg:p-8 rounded-2xl border border-primary-100">
                      <p className="text-base sm:text-lg text-gray-800 leading-relaxed">
                        {campaignData.description}
                      </p>
                    </div>
                  </motion.div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    >
                      <Card className="h-full border-0 shadow-lg bg-gradient-to-br from-red-50 to-orange-50">
                        <CardHeader>
                          <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900 flex flex-col sm:flex-row sm:items-center">
                            <div className="p-2 sm:p-3 bg-red-100 rounded-xl mr-0 sm:mr-3 mb-2 sm:mb-0 self-start">
                              <Target className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                            </div>
                            The Challenge
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                            {campaignData.problemStatement}
                          </p>
                        </CardContent>
                      </Card>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.4 }}
                    >
                      <Card className="h-full border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-green-50">
                        <CardHeader>
                          <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900 flex flex-col sm:flex-row sm:items-center">
                            <div className="p-2 sm:p-3 bg-emerald-100 rounded-xl mr-0 sm:mr-3 mb-2 sm:mb-0 self-start">
                              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                            </div>
                            Our Solution
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                            {campaignData.solution}
                          </p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="border-t border-gray-200 pt-6 sm:pt-10"
                  >
                    <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                      <CardHeader>
                        <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900 flex flex-col sm:flex-row sm:items-center">
                          <div className="p-2 sm:p-3 bg-blue-100 rounded-xl mr-0 sm:mr-3 mb-2 sm:mb-0 self-start">
                            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                          </div>
                          Stay in the Loop
                        </CardTitle>
                        <CardDescription className="text-base sm:text-lg text-gray-600">
                          Be the first to know about exciting updates and
                          developments
                        </CardDescription>
                        {waitlistCount && (
                          <div className="text-xs sm:text-sm text-blue-600 font-medium">
                            {waitlistCount.count} people are already subscribed
                          </div>
                        )}
                      </CardHeader>
                      <CardContent>
                        <form
                          onSubmit={handleSubmitEmail}
                          className="flex flex-col sm:flex-row gap-3 sm:gap-4"
                        >
                          <Input
                            type="email"
                            placeholder="Enter your email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 h-10 sm:h-12 text-base sm:text-lg border-2 border-blue-200 focus:border-blue-400 rounded-xl"
                            disabled={subscribeToWaitlistMutation.isPending}
                          />
                          <Button
                            type="submit"
                            size="lg"
                            disabled={
                              !email || subscribeToWaitlistMutation.isPending
                            }
                            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 px-4 sm:px-8 text-sm sm:text-base"
                          >
                            {subscribeToWaitlistMutation.isPending ? (
                              <>
                                <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-1.5 sm:mr-2" />
                                <span className="hidden sm:inline">
                                  Subscribing...
                                </span>
                                <span className="sm:hidden">Subscribing</span>
                              </>
                            ) : (
                              <>
                                <span className="hidden sm:inline">
                                  Subscribe
                                </span>
                                <span className="sm:hidden">Subscribe</span>
                                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1.5 sm:ml-2" />
                              </>
                            )}
                          </Button>
                        </form>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              </TabsContent>

              <TabsContent value="discuss" className="p-4 sm:p-6 lg:p-8">
                <div className="space-y-10">
                  <div className="flex flex-col sm:flex-row sm:items-center mb-6 sm:mb-8">
                    <div className="p-2 sm:p-3 bg-accent-100 rounded-xl mr-0 sm:mr-4 mb-3 sm:mb-0 self-start">
                      <MessageCircle className="w-6 h-6 sm:w-8 sm:h-8 text-accent-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Community Discussion
                      </h3>
                      <p className="text-gray-600 text-sm sm:text-base">
                        Share your thoughts, ask questions, and help this idea
                        bloom with your insights
                      </p>
                    </div>
                  </div>

                  {/* Comments Section */}
                  <div className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl rounded-2xl overflow-hidden">
                    <div className="p-4 sm:p-6 lg:p-8">
                      <CommentThread
                        comments={campaignComments || []}
                        campaignId={campaignData.id}
                        onCommentsChange={() => {
                          refetchComments();
                          // Also refresh campaign data to update comment count
                          window.location.reload();
                        }}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="pricing" className="p-4 sm:p-6 lg:p-8">
                <div className="space-y-10">
                  <div className="flex flex-col sm:flex-row sm:items-center mb-6 sm:mb-8">
                    <div className="p-2 sm:p-3 bg-emerald-100 rounded-xl mr-0 sm:mr-4 mb-3 sm:mb-0 self-start">
                      <ChartBar className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Pricing Feedback
                      </h3>
                      <p className="text-gray-600 text-sm sm:text-base">
                        Help us set the right price point for this product
                      </p>
                    </div>
                  </div>

                  {/* Enhanced Pricing Feedback Form */}
                  <PricingFeedbackForm
                    campaignId={campaignData.id}
                    pricingOptions={campaignData.pricingOptions || []}
                    onSuccess={() => {
                      // Refresh pricing feedback data
                      window.location.reload();
                    }}
                  />

                  {/* Pricing Feedback Summary */}
                  {pricingFeedback.length > 0 && (
                    <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-xl">
                          <Users className="w-5 h-5 text-blue-600" />
                          Community Pricing Insights
                        </CardTitle>
                        <p className="text-gray-600">
                          {pricingFeedback.length} people have shared their
                          pricing thoughts
                        </p>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
                          {[
                            {
                              sentiment: "LOVE",
                              label: "Love it",
                              emoji: "😍",
                              color: "text-red-500",
                            },
                            {
                              sentiment: "LIKE",
                              label: "Like it",
                              emoji: "🙂",
                              color: "text-green-500",
                            },
                            {
                              sentiment: "NEUTRAL",
                              label: "Neutral",
                              emoji: "😐",
                              color: "text-gray-500",
                            },
                            {
                              sentiment: "EXPENSIVE",
                              label: "Too expensive",
                              emoji: "😕",
                              color: "text-yellow-500",
                            },
                            {
                              sentiment: "NOT_INTERESTED",
                              label: "Not interested",
                              emoji: "👎",
                              color: "text-red-400",
                            },
                          ].map((option) => {
                            const count = pricingFeedback.filter(
                              (feedback) =>
                                feedback.sentiment === option.sentiment
                            ).length;
                            const percentage =
                              pricingFeedback.length > 0
                                ? Math.round(
                                    (count / pricingFeedback.length) * 100
                                  )
                                : 0;

                            return (
                              <div
                                key={option.sentiment}
                                className="text-center"
                              >
                                <div className="text-2xl mb-2">
                                  {option.emoji}
                                </div>
                                <div className={`font-bold ${option.color}`}>
                                  {count}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {option.label}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {percentage}%
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* Enhanced Playground Tab */}
              <TabsContent value="playground" className="p-4 sm:p-6 lg:p-8">
                <div className="space-y-10">
                  <div className="flex flex-col sm:flex-row sm:items-center mb-6 sm:mb-8">
                    <div className="p-2 sm:p-3 bg-blue-100 rounded-xl mr-0 sm:mr-4 mb-3 sm:mb-0 self-start">
                      <Image className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Campaign Resources
                      </h3>
                      <p className="text-gray-600 text-sm sm:text-base">
                        {isCreator
                          ? "Showcase your work with images, videos, and links to help others understand your vision."
                          : "Explore additional resources to better understand this innovative idea."}
                      </p>
                    </div>
                  </div>

                  {/* Add New Asset Form - Only for creator */}
                  {isCreator && (
                    <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                      <CardHeader>
                        <CardTitle className="text-xl font-bold flex items-center">
                          <Upload className="w-6 h-6 mr-3 text-blue-600" />
                          Add New Resource
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
                          {["image", "video", "link"].map((type) => (
                            <Button
                              key={type}
                              type="button"
                              variant={
                                newAsset.type === type ? "default" : "outline"
                              }
                              onClick={() =>
                                setNewAsset((prev) => ({
                                  ...prev,
                                  type: type as
                                    | "image"
                                    | "video"
                                    | "link"
                                    | "file",
                                }))
                              }
                              className="text-xs sm:text-sm"
                            >
                              {type === "image" && (
                                <Image className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                              )}
                              {type === "video" && (
                                <Video className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                              )}
                              {type === "link" && (
                                <LinkIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                              )}
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </Button>
                          ))}

                          <Button
                            type="button"
                            variant={
                              newAsset.type === "file" ? "default" : "outline"
                            }
                            onClick={triggerFileInput}
                            className="text-xs sm:text-sm"
                          >
                            <Upload className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            Upload File
                          </Button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileUpload}
                            className="hidden"
                            accept="image/*,video/*,application/*"
                          />
                        </div>

                        {newAsset.type !== "file" && (
                          <Input
                            type="text"
                            placeholder={`Enter ${newAsset.type} URL`}
                            value={newAsset.url}
                            onChange={(e) =>
                              setNewAsset((prev) => ({
                                ...prev,
                                url: e.target.value,
                              }))
                            }
                            className="h-10 sm:h-12 text-base sm:text-lg border-2 border-blue-200 focus:border-blue-400 rounded-xl"
                          />
                        )}

                        {newAsset.url && (
                          <div className="grid gap-3 sm:gap-4">
                            <Input
                              type="text"
                              placeholder="Enter a descriptive title"
                              value={newAsset.title}
                              onChange={(e) =>
                                setNewAsset((prev) => ({
                                  ...prev,
                                  title: e.target.value,
                                }))
                              }
                              className="h-10 sm:h-12 text-base sm:text-lg border-2 border-blue-200 focus:border-blue-400 rounded-xl"
                            />

                            <Textarea
                              placeholder="Describe what this asset demonstrates (optional)"
                              value={newAsset.description}
                              onChange={(e) =>
                                setNewAsset((prev) => ({
                                  ...prev,
                                  description: e.target.value,
                                }))
                              }
                              className="border-2 border-blue-200 focus:border-blue-400 rounded-xl text-sm sm:text-base"
                            />
                          </div>
                        )}

                        {newAsset.url && (
                          <Button
                            type="button"
                            onClick={handleAddAsset}
                            size="lg"
                            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-sm sm:text-base"
                          >
                            <Upload className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                            Add Resource
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Enhanced Asset Gallery */}
                  {assetsLoading ? (
                    <div className="text-center py-16 text-lg text-gray-500">
                      Loading resources...
                    </div>
                  ) : campaignAssets.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {campaignAssets.map((asset) => {
                        const frontendAsset = {
                          ...asset,
                          type: mapAssetType(asset.type) as
                            | "image"
                            | "video"
                            | "link"
                            | "file",
                        };
                        return (
                          <motion.div
                            key={asset.id}
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            whileHover="hover"
                            className="group"
                          >
                            <Card className="h-full border-0 shadow-lg bg-white/95 backdrop-blur-sm overflow-hidden rounded-2xl relative">
                              <CardHeader className="pb-4">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <CardTitle className="text-lg font-bold text-gray-900 mb-2">
                                      {asset.title}
                                    </CardTitle>
                                    {asset.description && (
                                      <CardDescription className="text-gray-600">
                                        {asset.description}
                                      </CardDescription>
                                    )}
                                  </div>
                                  {isCreator && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={async () => {
                                        await campaignsApi.deleteAsset(
                                          asset.id
                                        );
                                        refetchAssets();
                                        toast({
                                          description: "Asset has been removed",
                                        });
                                      }}
                                      className="text-red-500 hover:bg-red-50 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </CardHeader>
                              <CardContent className="p-0">
                                {renderAssetPreview(frontendAsset)}
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : (
                    <Card className="border-2 border-dashed border-gray-300 rounded-2xl">
                      <CardContent className="text-center py-16">
                        <div className="text-6xl mb-6">📁</div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                          No Resources Yet
                        </h3>
                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                          {isCreator
                            ? "Start by adding images, videos, files or links to help explain your innovative idea."
                            : "The creator hasn't shared any additional resources yet. Check back later for updates!"}
                        </p>
                        {isCreator && (
                          <Button className="bg-primary hover:bg-primary/90">
                            <Upload className="w-5 h-5 mr-2" />
                            Add Your First Resource
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>

      {/* Status Management Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Update Campaign Status
            </DialogTitle>
            <DialogDescription>
              Choose the current status for your campaign. This will affect how
              users can interact with it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {statusOptions.map((status) => (
              <div
                key={status.value}
                className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedStatus === status.value
                    ? "border-primary bg-primary/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setSelectedStatus(status.value)}
              >
                <input
                  type="radio"
                  name="status"
                  value={status.value}
                  checked={selectedStatus === status.value}
                  onChange={() => setSelectedStatus(status.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className={`font-medium ${status.color}`}>
                    {status.label}
                  </div>
                  <div className="text-sm text-gray-600">
                    {status.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowStatusDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStatusUpdate}
              disabled={!selectedStatus}
              className="bg-primary hover:bg-primary/90"
            >
              Update Status
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignView;
